import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import {
    Subscription,
    Observable,
    map,
    take,
    combineLatest,
    distinctUntilChanged,
    shareReplay,
    switchMap,
    from,
    tap,
    firstValueFrom
} from 'rxjs';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { markFormTouched, snapshot } from '../global/utils';
import {
    AlertService,
    ArmyBuilderConfig,
    HttpClientWithInFlightCache,
    LogMessage,
    revenueCatCustomerBaseUrl,
    UserService
} from '../global';

import { SupportService } from './support.service';
import { updateIssue } from './support.actions';
import { selectIssueById } from './support.selectors';
import { IssueUpdate, SupportIssue, SupportMode } from './model';
import { Force, ForceService } from '../forces';
import { ToastService } from '../global/toast/toast';

dayjs.extend(relativeTime);

@Component({
    standalone: false,
    selector: 'abs-issue-detail',
    template: `<abs-modal-layout>
        <!-- (close)="this.onDismiss(this.issue)" -->
        <ion-spinner *ngIf="loading"></ion-spinner>
        <ng-container *ngIf="issue$ | async as issue">
            <p><a href="#" (click)="toggleRaw($event)">Toggle Raw Data</a></p>
            <ng-container *ngIf="!showRaw">
                <p>
                    Status: <abs-issue-status [status]="issue.status"></abs-issue-status>
                    <strong>{{ issue.status }}</strong>
                </p>
                <p>
                    Page: <a [href]="issue.url" target="_blank">{{ issue.url }}</a>
                </p>
                <p>
                    Reported <strong><abs-relative-date [date]="issue.createdAt"></abs-relative-date></strong> by
                    <strong>{{ issue.userData?.user?.name }}</strong>
                </p>

                <div class="buttons" *ngIf="mode !== 'user'">
                    <ion-button (click)="viewUserInRC()">View User in RevenueCat</ion-button>
                    <ion-button *ngIf="issue.forces?.length > 0" (click)="copyUserForces(issue)"
                        >Copy {{ issue.forces?.length }} User Forces</ion-button
                    >
                </div>
                <pre>{{ issue.description }}</pre>
            </ng-container>

            @if (showRaw) {
                <div
                    class="raw"
                    *ngLet="{ reporter: reporter$ | async | json, issue: issue | json, logMessages: logMessages$ | async | json } as raw"
                >
                    <h2>
                        Reporter <span>(<a (click)="copyText(raw.reporter)">copy</a>)</span>
                    </h2>
                    <textarea disabled>{{ raw.reporter }}</textarea>
                    <h2>
                        Issue <span>(<a (click)="copyText(raw.issue)">copy</a>)</span>
                    </h2>
                    <textarea disabled>{{ raw.issue }}</textarea>
                    <h2>
                        Log <span>(<a (click)="copyText(raw.logMessages)">copy</a>)</span>
                    </h2>
                    <textarea disabled>{{ raw.logMessages }}</textarea>
                </div>
            }

            <form *ngIf="issueForm" [formGroup]="issueForm" (ngSubmit)="onSubmit()">
                <div *ngIf="mode !== 'user'">
                    <ion-label>Status:</ion-label>
                    <ion-select formControlName="issueStatus">
                        <ion-select-option [value]="'New'">New</ion-select-option>
                        <ion-select-option [value]="'In progress'">In progress</ion-select-option>
                        <ion-select-option [value]="'Resolved'">Resolved</ion-select-option>
                        <ion-select-option [value]="'Rejected'">Rejected</ion-select-option>
                        <ion-select-option [value]="'Waiting for customer'">Waiting for customer</ion-select-option>
                        <ion-select-option [value]="'Customer responded'">Customer responded</ion-select-option>
                    </ion-select>
                </div>
                <div>
                    <ion-label>Comment:</ion-label>
                    <ion-textarea formControlName="comment"></ion-textarea>
                </div>
                <ion-button [disabled]="!issueForm.valid || issueForm.pristine" type="submit">{{
                    mode === 'user' ? 'Comment' : 'Update Issue'
                }}</ion-button>
            </form>

            <ng-container *ngIf="!showRaw">
                <h2>Updates:</h2>
                <ng-template #updateTemplate let-update="item" let-commentIndex="index">
                    <div class="issueUpdate" *ngIf="update.description">
                        <ion-icon
                            class="deleteComment"
                            name="trash-outline"
                            *ngIf="mode === 'admin'"
                            (click)="deleteComment(issue, commentIndex)"
                        ></ion-icon>
                        <p>
                            Updated
                            <strong><abs-relative-date [date]="update.updateDate"></abs-relative-date></strong> by
                            <strong>{{ update.updatedBy }}</strong>
                        </p>
                        <p *ngIf="update.issueStatus">New status: {{ update.issueStatus }}</p>
                        <pre>{{ update.description }}</pre>
                    </div>
                    <div *ngIf="!update.description">
                        <strong>{{ update.updatedBy }}</strong> set the ticket status to <strong>{{ update.issueStatus }}</strong>
                    </div>
                </ng-template>
                <abs-list [items]="issue.updates" [template]="updateTemplate"></abs-list>
            </ng-container>
        </ng-container>
    </abs-modal-layout>`,
    styles: [
        `
            pre {
                max-height: 300px;
                overflow: auto;
                border: 1px solid #e0e0e0;
                padding: 10px;
                width: 100%;
                white-space: break-spaces;
            }

            .issueUpdate {
                width: 100%;
                padding: 12px 0;
            }

            .issueUpdate p,
            .issueUpdate pre {
                margin: 0;
            }

            .raw textarea {
                width: 100%;
                max-height: 300px;
                overflow: auto;
                border: 1px solid #e0e0e0;
                font-family: monospace;
                padding: 10px;
                white-space: break-spaces;
            }

            .raw h2 span {
                color: blue;
                cursor: pointer;
                text-decoration: underline;
                font-size: 0.8em;
            }

            .deleteComment {
                position: absolute;
                right: 10px;
                top: 10px;
            }
        `
    ]
})
export class IssueDetailComponent implements OnInit, OnDestroy {
    @Input()
    issueId: string = null;

    issue$: Observable<SupportIssue>;
    logMessages$: Observable<LogMessage[]>;
    userEmail$ = this.userService.login$.pipe(
        map((l) => l.user.email),
        distinctUntilChanged()
    );

    reporter$: Observable<any>;

    @Input()
    onDismiss: (res: any) => void = (_) => {};

    @Input()
    mode: SupportMode = 'admin';

    issueFormSub: Subscription;
    issueForm: UntypedFormGroup;
    showRaw = false;
    loading = false;

    constructor(
        private config: ArmyBuilderConfig,
        private fb: UntypedFormBuilder,
        private supportService: SupportService,
        private store: Store,
        private httpClient: HttpClientWithInFlightCache,
        private userService: UserService,
        private forceService: ForceService,
        private toast: ToastService,
        private alert: AlertService
    ) {}
    async ngOnInit() {
        this.loading = true;
        this.loadIssue();

        this.reporter$ = this.issue$.pipe(
            map((issue) => issue.userData?.user?.email || ''),
            distinctUntilChanged(),
            switchMap((email) => from(this.userService.lookupUser(email))),
            shareReplay(1)
        );

        combineLatest([this.userEmail$, this.issue$])
            .pipe(take(1))
            .subscribe(([userEmail, issue]) => {
                if (userEmail === issue.reporterEmail) {
                    this.supportService.markIssueRead(issue._id);
                }
            });

        this.issueFormSub = this.issue$.subscribe((issue) => {
            if (!issue) {
                return;
            }

            const comment = [null];
            if (this.mode !== 'admin') {
                comment.push(Validators.required);
            }

            this.issueForm = this.fb.group({
                comment,
                issueStatus: [issue.status, Validators.required]
            });
        });
    }

    loadIssue() {
        const issue$ = this.supportService.getIssueById(this.issueId).pipe(tap(() => (this.loading = false)));

        this.issue$ = issue$.pipe(
            map((issue) => {
                let res = {
                    ...issue,
                    updates: [...issue.updates].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime())
                };
                delete res.console;
                return res;
            })
        );

        this.logMessages$ = issue$.pipe(map((issue) => issue.console));
    }

    onSubmit() {
        markFormTouched(this.issueForm);

        if (this.issueForm.valid) {
            this.supportService.updateIssue(this.issueId, this.issueForm.getRawValue(), this.mode).subscribe((res) => {
                // this.issue = { ...res, updates: res.updates.sort((a, b) => b.date - a.date) };
                this.loadIssue();
                this.store.dispatch(updateIssue(res));
                this.issueForm.reset();
            });
        }
    }

    toggleRaw(e) {
        e.preventDefault();
        this.showRaw = !this.showRaw;
    }

    ngOnDestroy() {
        if (this.issueFormSub) {
            this.issueFormSub.unsubscribe();
        }
    }

    async viewUserInRC() {
        snapshot(this.reporter$, (reporter) => {
            if (reporter?._id) {
                window.open(revenueCatCustomerBaseUrl(this.config.revenueCatAppId) + reporter._id, '_blank');
            }
        });
    }

    copyUserForces(issue: SupportIssue) {
        for (let f of issue.forces) {
            this.forceService.copyForce({ ...f, impersonated: true, originalUserEmail: issue.reporterEmail });
        }

        this.toast.showToast('Success', `Copied ${issue.forces.length} forces to state`, { color: 'success', duration: 3000 });
    }

    copyText(text: string) {
        navigator.clipboard.writeText(text);
        this.toast.showToast('Success', 'Copied to clipboard', { color: 'success', duration: 3000 });
    }

    async deleteComment(issue: SupportIssue, commentIndex: number) {
        if (commentIndex === -1 || commentIndex === undefined || commentIndex === null) {
            return;
        }

        let confirm = await this.alert.showConfirm(
            'Delete Comment',
            "Are you sure you want to delete this comment?  This can't be undone."
        );

        if (confirm.role === 'cancel') {
            return;
        }

        let actualCommentIndex = issue.updates.length - commentIndex - 1; // Because comments are displayed in reverse order
        this.supportService.deleteIssueComment(this.issueId, actualCommentIndex).subscribe(() => {
            this.loadIssue();
        });
    }
}
